import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useGetOrganizerById } from "lib/hooks";
import { useParams } from "react-router-dom";
import { Form, Input, Select } from "antd";
import { ReactComponent as Completed } from "images/admin/completedicon.svg";
import { ReactComponent as Total } from "images/admin/totalamount.svg";

const Section = styled.div`
  margin-top: 1rem;
  margin-left: 10%;
  margin-right: 10%;
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    margin-top: 3rem;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    width: 90%;
  }
`;

const Top = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;

const Middle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 5px;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  width: 100%;
`;

const TopItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const TopItemTop = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const TopItemBottom = styled.div`
  display: flex;
  flex-direction: row;
`;

const ItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
`;

const SmallTextAsh = styled.p`
  text-align: left;
  font-size: 14px;
  margin-top: 0;
  margin-bottom: 0;
  color: #595959;
`;

const SmallTextBlack = styled.p`
  text-align: left;
  font-size: 14px;
  margin-top: 0;
  margin-bottom: 0;
`;

const VerticalDivider = styled.div`
  height: 50px;
  width: 1px;
  background-color: #ede9e9;
  margin: 0 10px;
`;

const Bottom = styled.div`
  margin-top: 2rem;
  width: 100%;
`;

const MiddleItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemTop = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemBottom = styled.div``;

const OrganizerName = styled.div`
  margin-top: 0;
  margin-bottom: 0;
`;

const LargeText = styled.p`
  text-align: left;
  font-size: 40px;
  font-weight: 600;
  line-height: 48px;
  text-align: left;
  margin-top: 0;
  margin-bottom: 0;
  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 0;
  }
`;

const Circle = styled.div<{ imageUrl?: string }>`
  width: 20vw;
  height: 20vw;
  max-width: 128px;
  max-height: 128px;
  border-radius: 50%;
  background-color: ${({ imageUrl }) => (imageUrl ? "transparent" : "#1390e6")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  position: relative;
  background-image: ${({ imageUrl }) => (imageUrl ? `url(${imageUrl})` : "none")};
  background-size: cover;
  background-position: center;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    width: 40vw;
    height: 40vw;
    font-size: 0.9rem;
  }
`;

const RowContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
`;

const FormBox = styled.div`
  margin-bottom: 10px;
  width: 100%;
`;

const DisabledInput = styled(Input)`
  background-color: #f5f5f5;
  color: rgba(0, 0, 0, 0.85);
  cursor: not-allowed;
  width: 100%;
  font-size: 14px;
  color: #9c9c9c;
`;

const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const AdminOrganizerProfile: React.FC = () => {
  const params = useParams<{ id?: string }>();
  const [organizerId, setOrganizerId] = useState<number>(0);
  const [form] = Form.useForm();
  const [name, setName] = useState<string>("");
  const [initials, setInitials] = useState<string>("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    const id = params.id ? parseInt(params.id) : undefined;
    if (id) setOrganizerId(id);
  }, [params.id]);

  const { data: organizer, isLoading, error } = useGetOrganizerById({ id: organizerId });

  // if (isLoading) return <div>Loading...</div>;
  // if (error) return <div>Error loading organizer data</div>;
  // if (!organizer) return <div>Organizer not found</div>;

  useEffect(() => {
    if (organizer) {
      const fullName = `${capitalizeFirstLetter(organizer.user.firstName)} ${capitalizeFirstLetter(organizer.user.lastName)}`;
      setName(fullName);
      setInitials(
        `${organizer.user.firstName.charAt(0).toUpperCase()}${organizer.user.lastName.charAt(0).toUpperCase()}`,
      );

      if (organizer.avatar) {
        setAvatarUrl(organizer.avatar);
      }
    }
  }, [organizer]);

  const getLocationString = () => {
    const parts = [];
    if (organizer?.city) parts.push(organizer.city);
    if (organizer?.state) parts.push(organizer.state);
    if (organizer?.country) parts.push(organizer.country);

    return parts.length > 0 ? parts.join(", ") : "Not provided";
  };

  return (
    <Section>
      <Top>
        <Circle imageUrl={avatarUrl || undefined}>{!avatarUrl && initials}</Circle>
        <OrganizerName>
          <LargeText>
            {organizer?.user?.firstName} {organizer?.user?.lastName}
          </LargeText>
        </OrganizerName>
      </Top>
      <Middle>
        <TopItem>
          <TopItemTop>
            <Completed /> <SmallTextAsh>Completed requests</SmallTextAsh>
          </TopItemTop>
          <TopItemBottom>
            <SmallTextBlack>8 requests</SmallTextBlack>
          </TopItemBottom>
        </TopItem>
        <ItemContainer>
          <VerticalDivider />
          <TopItem>
            <TopItemTop>
              <Total />
              <SmallTextAsh>Total amount</SmallTextAsh>
            </TopItemTop>
            <TopItemBottom>
              <SmallTextBlack>NGN 30,000,000</SmallTextBlack>
            </TopItemBottom>
          </TopItem>
        </ItemContainer>
        <ItemContainer>
          <VerticalDivider />
          <TopItem>
            <TopItemTop>
              <Total />
              <SmallTextAsh>Joining Date</SmallTextAsh>
            </TopItemTop>
            <TopItemBottom>
              <SmallTextBlack>26 Dec 2023 at 04:00 PM</SmallTextBlack>
            </TopItemBottom>
          </TopItem>
        </ItemContainer>
      </Middle>
      <Bottom>
        <Form layout="vertical" form={form} initialValues={{ remember: true }}>
          <RowContainer>
            <FormBox style={{ flex: 1 }}>
              <Form.Item label="First Name">
                <DisabledInput value={organizer?.user?.firstName} disabled style={{ paddingLeft: "0.5rem" }} />
              </Form.Item>
            </FormBox>
            <FormBox style={{ flex: 1 }}>
              <Form.Item label="Last Name">
                <DisabledInput value={organizer?.user?.lastName} disabled style={{ paddingLeft: "0.5rem" }} />
              </Form.Item>
            </FormBox>
          </RowContainer>

          <FormBox>
            <Form.Item label="Email Address">
              <DisabledInput value={organizer?.user?.email} disabled style={{ paddingLeft: "0.5rem" }} />
            </Form.Item>
          </FormBox>

          <FormBox>
            <Form.Item label="Phone Number">
              <DisabledInput
                value={organizer?.user.phone || "Not provided"}
                disabled
                style={{ paddingLeft: "0.5rem" }}
              />
            </Form.Item>
          </FormBox>

          <FormBox>
            <Form.Item label="Organization Name">
              <DisabledInput
                value={organizer?.organizationName || "Not provided"}
                disabled
                style={{ paddingLeft: "0.5rem" }}
              />
            </Form.Item>
          </FormBox>

          <FormBox>
            <Form.Item label="Organization Category">
              <DisabledInput
                value={organizer?.organization?.category || "Not provided"}
                disabled
                style={{ paddingLeft: "0.5rem" }}
              />
            </Form.Item>
          </FormBox>
          <FormBox>
            <Form.Item label="Location">
              <DisabledInput value={getLocationString()} disabled style={{ paddingLeft: "0.5rem" }} />
            </Form.Item>
          </FormBox>
        </Form>
      </Bottom>
    </Section>
  );
};

export default AdminOrganizerProfile;
